import React, { useState, useEffect, useCallback, useRef } from 'react';
import { kpiApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * The marking ladder — "what counts as 4 marks, what counts as 2".
 *
 * For most KPIs this rule has only ever existed as a sentence in the master:
 *
 *     100% SAME DAY 4 MARKS FOR 100% , SAME DAY=4, NEXT DAY=3,NEXT2DAYS=2...
 *
 * Nothing could execute that, nobody could edit it safely, and the sentence
 * routinely disagreed with the band table underneath it. Here the numbers are
 * the input and THE SENTENCE IS GENERATED, so the two cannot come apart.
 *
 * Every calculation on this screen is the server's. `/kpi/bands/preview` runs
 * `_match_band`, `_score_bands` and `_clamp_marks` — the same three the monthly
 * job runs — so "1840 scores 2" here means it scores 2 in an appraisal. A
 * second copy of the rule living in JavaScript is the exact problem this
 * rebuild exists to remove, so there isn't one.
 *
 * Marks may be negative. Five KPIs (in-house rejection, customer complaints,
 * suggestion participation, both hold-material ageing rules) carry agreed
 * penalty ladders, and a floor of 0 would quietly delete them.
 */

const KIND = { gte: 'At least', lte: 'Up to', else: 'Anything else' };

const kindOf = (b) => ('gte' in b ? 'gte' : 'lte' in b ? 'lte' : 'else');
const thresholdOf = (b) => (b.gte ?? b.lte ?? '');

// The shared ladder every plant falls back to. Matches BANDS_SCOPE_ALL on the
// server; no real plant could be called this.
export const SCOPE_ALL = '__all__';

/**
 * Which plants the ladder below belongs to.
 *
 * Compact and top-right, beside the heading, because it qualifies the table
 * rather than being a step before it — a reader who ignores it is still
 * looking at the shared ladder, which is what it has always been.
 *
 * "All Plants" and a named plant cannot both be on: the first means "every
 * plant that has no ladder of its own", the second means "this plant only",
 * and a selection holding both has two readings and no way to choose. Picking
 * a plant therefore drops All Plants, and picking All Plants clears the rest.
 */
function ScopePicker({ plants, mode, scope, overrides, onMode, onPick, testId }) {
  if (!plants.length) return null;
  const chip = (on) => `text-[11px] rounded-full px-2.5 py-0.5 border transition-colors ${on
    ? 'bg-[#E8EFFD] border-[#2563EB] text-[#172554] font-medium'
    : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:bg-[#F5F8FF]'}`;
  const MODES = [
    ['all', 'All Plants', 'One shared ladder. Every plant uses it unless that plant has its own.'],
    ['one', 'Single Plant', 'Edit one plant’s own ladder. No other plant changes.'],
    ['many', 'Same Ladder for Selected Plants',
     'Copy one ladder to the plants you tick. They stay independent afterwards.'],
  ];
  return (
    <div className="flex flex-col items-end gap-1" data-testid={`${testId}-scope`}>
      <div className="flex items-center gap-1.5 flex-wrap justify-end">
        <span className="text-[10px] uppercase tracking-wide text-[#B4B9CC] mr-0.5">Applies to</span>
        {MODES.map(([m, label, help]) => (
          <button key={m} type="button" onClick={() => onMode(m)} title={help}
            className={chip(mode === m)} aria-pressed={mode === m}
            data-testid={`${testId}-mode-${m}`}>
            {m === 'many' ? 'Selected Plants' : label}
          </button>
        ))}
      </div>
      {/* The plant list appears only in the modes that need one, so All Plants
          stays a single press and does not make you scan past four plants to
          confirm you picked none of them. */}
      {mode === 'one' && (
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {plants.map((p) => (
            <button key={p.name} type="button" onClick={() => onPick([p.name])}
              className={chip(scope[0] === p.name)} aria-pressed={scope[0] === p.name}
              data-testid={`${testId}-plant-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              title={overrides.includes(p.name)
                ? `${p.name} has its own ladder.`
                : `${p.name} currently uses the shared ladder.`}>
              {p.short}
              {overrides.includes(p.name) && (
                <span className="ml-1 text-[#2563EB]" title="has its own ladder">•</span>
              )}
            </button>
          ))}
        </div>
      )}
      {mode === 'many' && (
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {plants.map((p) => (
            <label key={p.name}
              className="flex items-center gap-1 text-[11px] text-[#5A6377] cursor-pointer">
              <input type="checkbox" checked={scope.includes(p.name)}
                data-testid={`${testId}-check-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onChange={() => onPick(scope.includes(p.name)
                  ? scope.filter((x) => x !== p.name) : [...scope, p.name])} />
              {p.short}
              {overrides.includes(p.name) && <span className="text-[#2563EB]">•</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Which plants have a ladder of their own, read from what is STORED. */
function PlantStatus({ plants, overrides, testId }) {
  if (!plants.length) return null;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#98A1B2] mb-2"
      data-testid={`${testId}-plant-status`}>
      {plants.map((p) => (
        <span key={p.name}>
          {p.short} —{' '}
          <span className={overrides.includes(p.name) ? 'text-[#2563EB] font-medium' : ''}>
            {overrides.includes(p.name) ? 'Custom' : 'Shared'}
          </span>
        </span>
      ))}
    </div>
  );
}

export default function BandLadder({ bands, onChange, uom, testId = 'band-ladder',
                                     plants = [], scope = [], overrides = [],
                                     mode = 'all', onMode = null, onScope = null,
                                     inherited = false, onRemoveOverride = null }) {
  const rows = bands || [];
  const [preview, setPreview] = useState(null);
  const [test, setTest] = useState('');
  const timer = useRef(null);

  const refresh = useCallback(async (nextBands, testValue) => {
    try {
      const body = { bands: nextBands || [], uom: uom || '' };
      const n = Number(testValue);
      if (testValue !== '' && testValue !== null && Number.isFinite(n)) body.test_value = n;
      const r = await kpiApi.previewBands(body);
      setPreview(r.data);
    } catch { setPreview(null); }
  }, [uom]);

  // Debounced so typing a threshold digit by digit doesn't fire a call per
  // keystroke — and so a half-typed "2" in "2000" never flashes a wrong mark.
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => refresh(rows, test), 250);
    return () => clearTimeout(timer.current);
  }, [JSON.stringify(rows), test, refresh]);   // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (i, next) => {
    const copy = rows.map((b, j) => (j === i ? next : b));
    onChange(copy);
  };
  const setKind = (i, kind) => {
    const b = rows[i];
    const t = thresholdOf(b);
    const score = b.score;
    patch(i, kind === 'else' ? { else: true, score } : { [kind]: t === '' ? 0 : Number(t), score });
  };
  const setThreshold = (i, v) => {
    const b = rows[i];
    const kind = kindOf(b);
    if (kind === 'else') return;
    patch(i, { [kind]: v === '' ? '' : Number(v), score: b.score });
  };
  const setScore = (i, v) => {
    const b = rows[i];
    patch(i, { ...b, score: v === '' ? '' : Number(v) });
  };
  const addRow = () => {
    // A new ladder starts with a sensible shape rather than a blank row: the
    // top rung gives full marks, and the catch-all stays at the bottom where
    // it is the only place it can legally be.
    const hasElse = rows.some((b) => kindOf(b) === 'else');
    const fresh = { lte: '', score: '' };
    onChange(hasElse ? [...rows.slice(0, -1), fresh, rows[rows.length - 1]] : [...rows, fresh]);
  };
  const addCatchAll = () => onChange([...rows, { else: true, score: 0 }]);
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const copy = [...rows];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };

  const errors = (preview?.problems || []).filter((p) => p.level === 'error');
  const warnings = (preview?.problems || []).filter((p) => p.level === 'warning');
  const rowProblem = (i) => (preview?.problems || []).find((p) => p.index === i);

  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <label className="block text-[11px] uppercase text-[#98A1B2]">
          Marking ladder
          <span className="normal-case text-[#98A1B2]"> — checked top to bottom, first match wins</span>
        </label>
        {onScope && (
          <ScopePicker plants={plants} mode={mode} scope={scope} overrides={overrides}
            onMode={onMode} onPick={onScope} testId={testId} />
        )}
      </div>

      {/* WHAT IS BEING EDITED, as a sentence, above the table. The table looks
          identical whether it is the shared rule or one plant's own, and
          editing the wrong one is both easy and invisible. */}
      {onScope && (
        <div className="mb-2">
          <p className="text-[12px] font-medium text-[#172554]"
            data-testid={`${testId}-editing-target`}>
            {mode === 'all' ? 'Editing the shared ladder for: All Plants'
              : mode === 'one' ? (scope.length
                ? `Editing ladder for: ${scope[0]}`
                : 'Pick the plant whose ladder you want to edit')
              : (scope.length
                ? `Applying the same ladder to: ${scope.join(' + ')}`
                : 'Tick the plants that should get the same ladder')}
          </p>
          <p className={`text-[11px] ${inherited ? 'text-[#B14A0E]' : 'text-[#98A1B2]'}`}
            data-testid={`${testId}-scope-note`}>
            {mode === 'all' ? (
              overrides.length
                ? `Plants without their own ladder are marked against this. ${overrides.length} `
                  + `plant(s) have their own and are NOT changed by saving here: ${overrides.join(', ')}.`
                : 'Every plant is marked against this — none has a ladder of its own yet.'
            ) : !scope.length ? (
              mode === 'one' ? 'Nothing is being edited yet.'
                             : 'Tick at least two plants, or use Single Plant for one.'
            ) : inherited ? (
              `${scope.join(' and ')} ${scope.length > 1 ? 'have' : 'has'} no ladder of `
              + 'its own, so the shared one is shown as a starting point. Saving creates '
              + `a ladder that belongs only to ${scope.length > 1 ? 'those plants' : 'that plant'}; `
              + 'the shared ladder does not change.'
            ) : (
              `Saving changes only ${scope.join(' and ')}. The shared ladder and every `
              + 'other plant are left exactly as they are.'
            )}
          </p>
          {/* Copy-at-save, not a permanent link — the single most likely thing
              to be misread about ticking two plants. */}
          {mode === 'many' && scope.length > 1 && (
            <p className="text-[11px] text-[#98A1B2]">
              Each plant gets its own copy. Editing one of them later does not move the other.
            </p>
          )}
          <PlantStatus plants={plants} overrides={overrides} testId={testId} />
          {/* Removing an override is its own action. An empty table is never
              read as a request to delete one. */}
          {mode !== 'all' && scope.some((p) => overrides.includes(p)) && onRemoveOverride && (
            <button type="button" onClick={onRemoveOverride}
              data-testid={`${testId}-remove-override`}
              className="text-[11px] text-[#C2151F] hover:underline">
              Remove the custom ladder from {scope.filter((p) => overrides.includes(p)).join(' and ')}
              {' '}and use the shared ladder
            </button>
          )}
        </div>
      )}

      {!rows.length && (
        <p className="text-xs text-[#B14A0E] bg-[#FFF3E0] border border-[#FBD9A8] rounded-lg px-3 py-2 mb-2"
          data-testid={`${testId}-empty`}>
          No marking rule yet — this KPI cannot be scored automatically, whatever it is pointed at.
        </p>
      )}

      {!!rows.length && (
        <div className="rounded-lg border border-[#E6E9F0] bg-white overflow-hidden mb-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#98A1B2] text-[11px] uppercase">
                <th className="text-left font-semibold px-3 py-1.5 w-8" />
                <th className="text-left font-semibold px-3 py-1.5">Condition</th>
                <th className="text-left font-semibold px-3 py-1.5 w-28">Marks</th>
                <th className="text-left font-semibold px-3 py-1.5">Reads as</th>
                <th className="px-3 py-1.5 w-24" />
              </tr>
            </thead>
            <tbody>
              {rows.map((b, i) => {
                const kind = kindOf(b);
                const rung = preview?.rungs?.[i];
                const bad = rowProblem(i);
                return (
                  <tr key={i} data-testid={`${testId}-row-${i}`}
                    className={`border-t border-[#F2F4F8] ${rung?.matched ? 'bg-[#E8F7EE]' : ''}`}>
                    <td className="px-2 py-1.5 text-[11px] text-[#C9CFDC] text-center">{i + 1}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <select value={kind} onChange={(e) => setKind(i, e.target.value)}
                          data-testid={`${testId}-kind-${i}`}
                          className="text-sm border border-[#E6E9F0] rounded-md px-2 py-1 bg-white">
                          {Object.entries(KIND).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        {kind !== 'else' && (
                          <Input type="number" value={thresholdOf(b)} placeholder="0"
                            onChange={(e) => setThreshold(i, e.target.value)}
                            data-testid={`${testId}-threshold-${i}`}
                            className="h-8 w-32 text-sm tabular-nums" />
                        )}
                        {kind !== 'else' && uom && <span className="text-xs text-[#98A1B2]">{uom}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <Input type="number" value={b.score ?? ''} placeholder="0"
                        onChange={(e) => setScore(i, e.target.value)}
                        data-testid={`${testId}-score-${i}`}
                        className="h-8 w-20 text-sm tabular-nums font-semibold" />
                    </td>
                    <td className="px-3 py-1.5 text-xs">
                      {bad
                        ? <span className="text-[#C2151F]" data-testid={`${testId}-row-problem-${i}`}>{bad.text}</span>
                        : <span className="text-[#5A6377]">{rung?.condition || '—'}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                        className="text-[#98A1B2] hover:text-[#2563EB] disabled:opacity-25 px-1">↑</button>
                      <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} title="Move down"
                        className="text-[#98A1B2] hover:text-[#2563EB] disabled:opacity-25 px-1">↓</button>
                      <button onClick={() => removeRow(i)} title="Remove this step"
                        data-testid={`${testId}-remove-${i}`}
                        className="text-[#98A1B2] hover:text-[#C2151F] px-1">×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button size="sm" variant="outline" onClick={addRow} data-testid={`${testId}-add`}>+ Add a step</Button>
        {!rows.some((b) => kindOf(b) === 'else') && (
          <Button size="sm" variant="outline" onClick={addCatchAll} data-testid={`${testId}-add-else`}>
            + Add “anything else”
          </Button>
        )}
      </div>

      {/* Try it. The single most useful control here: put in last month's real
          number and see the mark it produces, before anyone is scored on it. */}
      {!!rows.length && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`${testId}-try`}>
          <span className="text-[11px] uppercase text-[#98A1B2]">Try it</span>
          <Input type="number" value={test} onChange={(e) => setTest(e.target.value)}
            placeholder="a real value" data-testid={`${testId}-test-input`}
            className="h-8 w-36 text-sm tabular-nums" />
          {uom && <span className="text-xs text-[#98A1B2]">{uom}</span>}
          <span className="text-[#C9CFDC]">→</span>
          {preview?.test ? (
            <>
              <span className="inline-block rounded-md px-2 py-0.5 font-bold tabular-nums"
                data-testid={`${testId}-test-marks`}
                style={{ background: preview.test.marks >= 0 ? '#E8EFFD' : '#FDE9EF',
                         color: preview.test.marks >= 0 ? '#2563EB' : '#C2151F' }}>
                {preview.test.marks} marks
              </span>
              {preview.test.fell_through
                ? <span className="text-xs text-[#C2151F]" data-testid={`${testId}-fell-through`}>
                    matched no step — scored 0 by default
                  </span>
                : <span className="text-xs text-[#5A6377]">via “{preview.test.matched_condition}”</span>}
            </>
          ) : <span className="text-xs text-[#C9CFDC]">enter a value</span>}
        </div>
      )}

      {errors.map((p, i) => (
        <p key={`e${i}`} data-testid={`${testId}-error`}
          className="text-xs text-[#C2151F] bg-[#FDE9EF] border border-[#F7C2CE] rounded-lg px-3 py-1.5 mb-1">
          {p.text}
        </p>
      ))}
      {warnings.map((p, i) => (
        <p key={`w${i}`} data-testid={`${testId}-warning`}
          className="text-xs text-[#B14A0E] bg-[#FFF3E0] border border-[#FBD9A8] rounded-lg px-3 py-1.5 mb-1">
          {p.text}
        </p>
      ))}

      {/* Written by the software from the numbers above, so it can never say
          something the ladder does not do. This is what prints on the sheet. */}
      {preview?.criteria && (
        <div className="mt-2 rounded-lg bg-[#F8FAFC] border border-[#E6E9F0] px-3 py-2">
          <div className="text-[11px] uppercase text-[#98A1B2] mb-0.5">Reads as</div>
          <div className="text-xs text-[#11182B] leading-snug" data-testid={`${testId}-criteria`}>
            {preview.criteria}
          </div>
        </div>
      )}
    </div>
  );
}
